import React, { useState, useEffect } from "react";
import Auth from "../Auth";
import { useParams, Link } from "react-router-dom";

export default function ExperimentDetail() {
  const { experimentId } = useParams();
  const [experimentData, setExperimentData] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});
  const [wordStatsExpanded, setWordStatsExpanded] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [metricEventFilter, setMetricEventFilter] = useState("");
  const [variantMetricEvents, setVariantMetricEvents] = useState([]);

  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedVariant2, setSelectedVariant2] = useState(null);
  const [cards2, setCards2] = useState([]);
  const [cardsLoading2, setCardsLoading2] = useState(false);
  const [pagination2, setPagination2] = useState({ page: 1, limit: 20, total: 0 });
  const [metricEventFilter2, setMetricEventFilter2] = useState("");
  const [variantMetricEvents2, setVariantMetricEvents2] = useState([]);

  useEffect(() => {
    fetchExperimentDetails();
  }, [experimentId]);

  useEffect(() => {
    if (selectedVariant) {
      // Reset to page 1 when variant or filter changes
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [selectedVariant, metricEventFilter]);

  useEffect(() => {
    if (selectedVariant && pagination.page) {
      fetchCards(pagination.page);
    }
  }, [selectedVariant, metricEventFilter, pagination.page]);

  // Comparison mode effects
  useEffect(() => {
    if (comparisonMode && selectedVariant2) {
      // Reset to page 1 when variant or filter changes
      setPagination2(prev => ({ ...prev, page: 1 }));
    }
  }, [selectedVariant2, metricEventFilter2, comparisonMode]);

  useEffect(() => {
    if (comparisonMode && selectedVariant2 && pagination2.page) {
      fetchCards2(pagination2.page);
    }
  }, [selectedVariant2, metricEventFilter2, comparisonMode, pagination2.page]);

  const fetchExperimentDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await Auth.fetchWithIdToken(
        `/api/admin/experiments/${experimentId}`,
        {}
      );
      setExperimentData(result);
      // Auto-select first variant
      if (result.variants && result.variants.length > 0) {
        setSelectedVariant(result.variants[0].variationId);
        // Auto-select second variant for comparison (if exists)
        if (result.variants.length > 1) {
          setSelectedVariant2(result.variants[1].variationId);
        }
      }
    } catch (err) {
      console.error("Error fetching experiment details:", err);
      setError(err.message || "Failed to load experiment details");
    } finally {
      setLoading(false);
    }
  };

  const fetchCards = async (page) => {
    if (!selectedVariant) return;

    setCardsLoading(true);
    try {
      const result = await Auth.fetchWithIdToken(
        `/api/admin/experiments/${experimentId}/cards`,
        {
          variationId: selectedVariant,
          page: page,
          limit: pagination.limit,
          metricEventType: metricEventFilter || undefined,
        }
      );
      setCards(result.cards || []);
      setPagination(result.pagination);
      setVariantMetricEvents(result.variantMetricEvents || []);
    } catch (err) {
      console.error("Error fetching cards:", err);
      // Show error message to user
      setCards([]);
      setPagination({ page: 1, limit: 20, total: 0, totalPages: 0 });
      setVariantMetricEvents([]);
    } finally {
      setCardsLoading(false);
    }
  };

  const fetchCards2 = async (page) => {
    if (!selectedVariant2) return;

    setCardsLoading2(true);
    try {
      const result = await Auth.fetchWithIdToken(
        `/api/admin/experiments/${experimentId}/cards`,
        {
          variationId: selectedVariant2,
          page: page,
          limit: pagination2.limit,
          metricEventType: metricEventFilter2 || undefined,
        }
      );
      setCards2(result.cards || []);
      setPagination2(result.pagination);
      setVariantMetricEvents2(result.variantMetricEvents || []);
    } catch (err) {
      console.error("Error fetching cards 2:", err);
      setCards2([]);
      setPagination2({ page: 1, limit: 20, total: 0, totalPages: 0 });
      setVariantMetricEvents2([]);
    } finally {
      setCardsLoading2(false);
    }
  };

  const toggleCardExpanded = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const toggleEventsExpanded = (cardId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  // Calculate statistical significance for A/B testing (z-test for two proportions)
  const calculateSignificance = (successA, totalA, successB, totalB) => {
    if (totalA === 0 || totalB === 0) return null;

    const pA = successA / totalA;
    const pB = successB / totalB;
    const pPool = (successA + successB) / (totalA + totalB);

    // Standard error
    const se = Math.sqrt(pPool * (1 - pPool) * (1 / totalA + 1 / totalB));

    if (se === 0) return null;

    // Z-score
    const z = (pB - pA) / se;

    // Two-tailed p-value approximation using standard normal distribution
    const pValue = 2 * (1 - normalCDF(Math.abs(z)));

    return {
      zScore: z,
      pValue: pValue,
      isSignificant: pValue < 0.05,
      confidenceLevel: pValue < 0.01 ? 99 : pValue < 0.05 ? 95 : null,
    };
  };

  // Cumulative distribution function for standard normal distribution
  const normalCDF = (x) => {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const d = 0.3989423 * Math.exp(-x * x / 2);
    const prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return x > 0 ? 1 - prob : prob;
  };

  const renderStatsModal = () => {
    if (!statsModalOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px",
        }}
        onClick={() => setStatsModalOpen(false)}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            maxHeight: "90vh",
            overflow: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={{ margin: 0, fontSize: "20px" }}>Statistical Significance Calculations</h2>
            <button
              onClick={() => setStatsModalOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#666",
                padding: "0 8px",
              }}
            >
              ×
            </button>
          </div>

          <div style={{ fontSize: "14px", lineHeight: "1.6", color: "#333" }}>
            <h3 style={{ fontSize: "16px", marginTop: "20px", marginBottom: "10px", color: "#1976d2" }}>
              Overview
            </h3>
            <p>
              We use a <strong>Z-test for two proportions</strong> to determine if the difference in event rates
              between variants is statistically significant. This test measures the probability that an observed
              difference occurred by chance.
            </p>

            <h3 style={{ fontSize: "16px", marginTop: "20px", marginBottom: "10px", color: "#1976d2" }}>
              Step-by-Step Methodology
            </h3>

            <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>1. Calculate Event Rates</h4>
              <p style={{ margin: "5px 0" }}>
                For each variant, we calculate the proportion of cards that triggered the event:
              </p>
              <pre style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "4px", fontSize: "13px", margin: "10px 0" }}>
p_baseline = cards_with_event_baseline / total_cards_baseline
p_variant = cards_with_event_variant / total_cards_variant
              </pre>
            </div>

            <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>2. Calculate Pooled Proportion</h4>
              <p style={{ margin: "5px 0" }}>
                The pooled proportion combines both samples under the null hypothesis (no difference):
              </p>
              <pre style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "4px", fontSize: "13px", margin: "10px 0" }}>
p_pool = (successes_baseline + successes_variant) /
         (total_baseline + total_variant)
              </pre>
              <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
                This represents the overall event rate across both variants.
              </p>
            </div>

            <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>3. Calculate Standard Error (SE)</h4>
              <p style={{ margin: "5px 0" }}>
                The standard error measures the uncertainty in our proportion difference:
              </p>
              <pre style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "4px", fontSize: "13px", margin: "10px 0" }}>
SE = sqrt(p_pool × (1 - p_pool) × (1/n_baseline + 1/n_variant))
              </pre>
              <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
                Larger sample sizes result in smaller standard errors (more precise estimates).
              </p>
            </div>

            <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>4. Calculate Z-score</h4>
              <p style={{ margin: "5px 0" }}>
                The Z-score measures how many standard errors the observed difference is from zero:
              </p>
              <pre style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "4px", fontSize: "13px", margin: "10px 0" }}>
z = (p_variant - p_baseline) / SE
              </pre>
              <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
                A Z-score of ±1.96 corresponds to a 95% confidence level, ±2.58 to 99%.
              </p>
            </div>

            <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>5. Calculate P-value (Two-tailed)</h4>
              <p style={{ margin: "5px 0" }}>
                Using the standard normal distribution (CDF), we calculate:
              </p>
              <pre style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "4px", fontSize: "13px", margin: "10px 0" }}>
p-value = 2 × (1 - Φ(|z|))
              </pre>
              <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
                The p-value represents the probability of observing this difference (or larger) if there
                were truly no difference between variants. We use a two-tailed test because we care about
                differences in either direction.
              </p>
            </div>

            <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>6. Determine Confidence Level</h4>
              <p style={{ margin: "5px 0" }}>
                The confidence level is calculated as:
              </p>
              <pre style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "4px", fontSize: "13px", margin: "10px 0" }}>
confidence = (1 - p-value) × 100%
              </pre>
            </div>

            <h3 style={{ fontSize: "16px", marginTop: "20px", marginBottom: "10px", color: "#1976d2" }}>
              Interpretation
            </h3>

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>P-value Range</th>
                  <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>Confidence</th>
                  <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>Interpretation</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>p &lt; 0.01</td>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>≥ 99%</td>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                    <strong>Highly significant</strong> - Very strong evidence of a real difference
                  </td>
                </tr>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>0.01 ≤ p &lt; 0.05</td>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>95-99%</td>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                    <strong>Significant</strong> - Strong evidence of a real difference
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>p ≥ 0.05</td>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>&lt; 95%</td>
                  <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                    <strong>Not significant</strong> - Insufficient evidence; difference could be due to chance
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 style={{ fontSize: "16px", marginTop: "20px", marginBottom: "10px", color: "#1976d2" }}>
              Important Notes
            </h3>

            <ul style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Statistical significance ≠ Practical significance:</strong> A result can be statistically
                significant but have a small effect size that may not matter in practice.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Sample size matters:</strong> Larger samples provide more reliable results and can detect
                smaller differences.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Multiple comparisons:</strong> When testing many events simultaneously, some may appear
                significant by chance. Consider using corrections like Bonferroni if needed.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Assumptions:</strong> This test assumes independent observations and sufficiently large
                sample sizes (typically n·p and n·(1-p) ≥ 5 for both groups).
              </li>
            </ul>

            <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "#e3f2fd", borderRadius: "6px", borderLeft: "4px solid #1976d2" }}>
              <strong>Example:</strong> If variant B shows a "97% conf." badge, it means we can be 97% confident
              that the observed difference in event rates is real and not due to random chance. In other words,
              there's only a 3% probability that this difference occurred by chance alone.
            </div>
          </div>
        </div>
      </div>
    );
  };

  const exportCardsToTxt = async (variantName, filterName, totalCards) => {
    if (!variantName || totalCards === 0) {
      alert("No cards to export");
      return;
    }

    try {
      // Fetch all cards for this variant/filter
      const allCards = [];
      const limit = 100; // Fetch in batches of 100
      const totalPages = Math.ceil(totalCards / limit);

      for (let page = 1; page <= totalPages; page++) {
        const result = await Auth.fetchWithIdToken(
          `/api/admin/experiments/${experimentId}/cards`,
          {
            variationId: variantName,
            page: page,
            limit: limit,
            metricEventType: filterName || undefined,
          }
        );
        allCards.push(...(result.cards || []));
      }

      if (allCards.length === 0) {
        alert("No cards found to export");
        return;
      }

      // Generate text content
      const displayFilterName = !filterName ? "All Cards" : (filterName === "no-papi" ? "No PAPI" : filterName);
      let textContent = `Experiment: ${experimentId}\n`;
      textContent += `Variant: ${variantName}\n`;
      textContent += `Filter: ${displayFilterName}\n`;
      textContent += `Total Cards: ${allCards.length}\n`;
      textContent += `Exported: ${new Date().toLocaleString()}\n`;
      textContent += `\n${"=".repeat(80)}\n\n`;

      allCards.forEach((card, index) => {
        textContent += `CARD ${index + 1} / ${allCards.length}\n`;
        textContent += `${"-".repeat(80)}\n`;
        textContent += `Card ID: ${card.id}\n`;

        if (card.source) textContent += `Source: ${card.source}\n`;
        if (card.words) textContent += `Words: ${card.words}\n`;
        if (card.createdAt) {
          textContent += `Created: ${new Date(card.createdAt._seconds * 1000).toLocaleString()}\n`;
        }
        if (card.invocationId) textContent += `Invocation ID: ${card.invocationId}\n`;

        // Metric Events
        if (card.metricEvents && Object.keys(card.metricEvents).length > 0) {
          textContent += `\nMetric Events:\n`;
          Object.entries(card.metricEvents).forEach(([type, count]) => {
            textContent += `  - ${type}: ${count}\n`;
          });
        }

        // Prompt
        if (card.prompt) {
          textContent += `\nPrompt: ${card.prompt}\n`;
        }

        // Object
        if (card.object) {
          textContent += `\nObject: ${card.object}\n`;
        }

        // Passage
        if (card.passage) {
          const passageText = typeof card.passage === 'string' ? card.passage : JSON.stringify(card.passage);
          textContent += `\nPassage: ${passageText}\n`;
        }

        // User Feedback from Likes
        if (card.userFeedback && card.userFeedback.length > 0) {
          textContent += `\nUser Feedback (${card.userFeedback.length}):\n`;
          card.userFeedback.forEach((feedback) => {
            const thumbs = feedback.value === 1 ? "👍 Thumbs Up" : "👎 Thumbs Down";
            textContent += `  ${thumbs}`;
            if (feedback.path && feedback.path.length > 0) {
              textContent += ` [${feedback.path.join(", ")}]`;
            }
            textContent += `\n`;
            textContent += `    "${feedback.feedback}"\n`;
          });
        }

        // Content
        textContent += `\nContent:\n`;
        let displayContent;
        if (!card.content) {
          displayContent = "(No content)";
        } else if (typeof card.content === "string") {
          displayContent = card.content;
        } else if (Array.isArray(card.content)) {
          displayContent = card.content.join("\n\n");
        } else if (typeof card.content === "object") {
          displayContent = JSON.stringify(card.content, null, 2);
        } else {
          displayContent = String(card.content);
        }
        textContent += displayContent + "\n";

        textContent += `\n${"=".repeat(80)}\n\n`;
      });

      // Create blob and download
      const blob = new Blob([textContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filterSuffix = filterName ? `_${filterName}` : "";
      link.download = `${experimentId}_${variantName}${filterSuffix}_cards.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting cards:", err);
      alert("Failed to export cards: " + err.message);
    }
  };

  const renderCardColumn = ({
    variant,
    cards,
    cardsLoading,
    pagination,
    setPagination,
    metricEventFilter,
    setMetricEventFilter,
    variantMetricEvents,
    columnTitle,
    isBaselineColumn,
    baselineMetricEvents,
    baselineTotalCards,
  }) => {
    const totalCards = pagination.total;

    return (
      <div style={{ flex: comparisonMode ? "1 1 48%" : "1 1 100%" }}>
        <h2>
          {columnTitle} {metricEventFilter && `(${metricEventFilter === 'no-papi' ? 'No PAPI' : metricEventFilter})`}
        </h2>

        {variantMetricEvents.length > 0 && (
          <div style={{ marginBottom: "15px", backgroundColor: "#f9f9f9", padding: "12px", borderRadius: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <strong style={{ fontSize: "14px" }}>Events:</strong>
              {!isBaselineColumn && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setStatsModalOpen(true);
                  }}
                  style={{
                    fontSize: "12px",
                    color: "#1976d2",
                    textDecoration: "none",
                    fontWeight: "500",
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                  onMouseOut={(e) => e.target.style.textDecoration = "none"}
                >
                  Info
                </a>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {variantMetricEvents.map((event) => {
                const percentage = totalCards > 0 ? ((event.uniqueCards / totalCards) * 100).toFixed(1) : 0;

                // Calculate comparison metrics if this is not the baseline and we have baseline data
                let comparisonMetrics = null;
                if (!isBaselineColumn && baselineMetricEvents && baselineTotalCards) {
                  const baselineEvent = baselineMetricEvents.find(e => e.type === event.type);
                  if (baselineEvent) {
                    const baselinePercentage = baselineTotalCards > 0
                      ? (baselineEvent.uniqueCards / baselineTotalCards) * 100
                      : 0;
                    const percentageDiff = percentage - baselinePercentage;
                    const relativeChange = baselinePercentage > 0
                      ? ((percentageDiff / baselinePercentage) * 100).toFixed(1)
                      : null;

                    // Calculate statistical significance
                    const significance = calculateSignificance(
                      baselineEvent.uniqueCards,
                      baselineTotalCards,
                      event.uniqueCards,
                      totalCards
                    );

                    comparisonMetrics = {
                      percentageDiff,
                      relativeChange,
                      significance,
                      baselineEvent,
                    };
                  }
                }

                return (
                  <div
                    key={event.type}
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <strong>{event.type}:</strong> {event.uniqueCards} cards
                      </div>
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        {percentage}%
                      </div>
                    </div>
                    {comparisonMetrics && (
                      <div style={{ marginTop: "4px", fontSize: "11px" }}>
                        <span style={{
                          color: comparisonMetrics.percentageDiff >= 0 ? '#2e7d32' : '#c62828',
                          fontWeight: '500'
                        }}>
                          {comparisonMetrics.percentageDiff >= 0 ? '+' : ''}{comparisonMetrics.percentageDiff.toFixed(1)}pp
                        </span>
                        {comparisonMetrics.relativeChange && (
                          <span style={{
                            color: comparisonMetrics.percentageDiff >= 0 ? '#2e7d32' : '#c62828',
                            marginLeft: '6px'
                          }}>
                            ({comparisonMetrics.relativeChange >= 0 ? '+' : ''}{comparisonMetrics.relativeChange}%)
                          </span>
                        )}
                        {comparisonMetrics.significance && (
                          <span
                            style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              backgroundColor: comparisonMetrics.significance.isSignificant ? '#e8f5e9' : '#f5f5f5',
                              color: comparisonMetrics.significance.isSignificant ? '#2e7d32' : '#999',
                              fontSize: '10px',
                              fontWeight: '600',
                            }}
                          >
                            {comparisonMetrics.significance.isSignificant
                              ? `${comparisonMetrics.significance.confidenceLevel}% conf.`
                              : `${((1 - comparisonMetrics.significance.pValue) * 100).toFixed(0)}% conf.`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Export Button */}
        {cards.length > 0 && !cardsLoading && (
          <div style={{ marginBottom: "15px", display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => exportCardsToTxt(variant, metricEventFilter, pagination.total)}
              style={{
                padding: "4px 10px",
                fontSize: "12px",
                backgroundColor: "#666",
                color: "white",
                border: "none",
                borderRadius: "3px",
                cursor: "pointer",
                fontWeight: "400",
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "#555"}
              onMouseOut={(e) => e.target.style.backgroundColor = "#666"}
            >
              Export All {pagination.total} Cards as TXT
            </button>
          </div>
        )}

        {cardsLoading ? (
          <p>Loading cards...</p>
        ) : cards.length === 0 ? (
          <p style={{ color: "#666" }}>No cards found for this variant.</p>
        ) : (
          <>
            <div style={{ marginBottom: "15px", color: "#666", fontSize: "14px" }}>
              Showing {cards.length} cards (Page {pagination.page} of {pagination.totalPages})
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              {cards.map((card) => renderCard(card))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  style={{
                    padding: "6px 12px",
                    fontSize: "13px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: pagination.page === 1 ? "#f5f5f5" : "white",
                    cursor: pagination.page === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>

                <span style={{ fontSize: "13px" }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  style={{
                    padding: "6px 12px",
                    fontSize: "13px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: pagination.page === pagination.totalPages ? "#f5f5f5" : "white",
                    cursor: pagination.page === pagination.totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderCard = (card) => {
    return (
      <div
        key={card.id}
        style={{
          padding: comparisonMode ? "15px" : "20px",
          backgroundColor: "#f9f9f9",
          border: "1px solid #ddd",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
            fontSize: "12px",
            color: "#666",
          }}
        >
          <div>
            <strong>Source:</strong> {card.source || "N/A"} |{" "}
            <strong>Words:</strong> {card.words || 0}
            {card.invocationId && (
              <>
                {" | "}
                <Link
                  to={`/admin/invocation/${card.invocationId}`}
                  style={{ color: "#0066cc", textDecoration: "none" }}
                >
                  Invocation
                </Link>
              </>
            )}
          </div>
          <div>
            {card.createdAt
              ? new Date(card.createdAt._seconds * 1000).toLocaleDateString()
              : "N/A"}
          </div>
        </div>

        {/* Metric Events */}
        {card.metricEvents && Object.keys(card.metricEvents).length > 0 && (
          <div style={{ marginBottom: "8px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {Object.entries(card.metricEvents).map(([type, count]) => (
              <span
                key={type}
                style={{
                  display: "inline-block",
                  padding: "2px 6px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "3px",
                  fontSize: "11px",
                  color: "#1976d2",
                }}
              >
                {type}: {count}
              </span>
            ))}
          </div>
        )}

        {card.prompt && (
          <div style={{ marginBottom: "8px", fontSize: "13px" }}>
            <strong>Prompt:</strong> <span style={{ color: "#555" }}>{card.prompt}</span>
          </div>
        )}

        {card.object && (
          <div style={{ marginBottom: "8px", fontSize: "13px" }}>
            <strong>Object:</strong> <span style={{ color: "#555" }}>{card.object}</span>
          </div>
        )}

        {card.passage && (
          <div style={{ marginBottom: "8px", fontSize: "13px" }}>
            <strong>Passage:</strong>{" "}
            <span style={{ color: "#555", fontStyle: "italic" }}>
              {typeof card.passage === 'string' ? card.passage : JSON.stringify(card.passage)}
            </span>
          </div>
        )}

        {/* User Feedback Events */}
        {card.events && card.events.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ fontSize: "12px" }}>Feedback Events:</strong>
            <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "3px" }}>
              {(expandedEvents[card.id] ? card.events : card.events.slice(0, 3)).map((event) => (
                <div
                  key={event.id}
                  style={{
                    fontSize: "11px",
                    padding: "3px 6px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "3px",
                  }}
                >
                  <strong
                    style={{
                      color:
                        event.eventType === "like"
                          ? "#4caf50"
                          : event.eventType === "dislike"
                          ? "#f44336"
                          : "#666",
                    }}
                  >
                    {event.eventType}
                  </strong>
                  {event.path && event.path.length > 0 && (
                    <span style={{ color: "#999", marginLeft: "4px" }}>
                      [{event.path.join(", ")}]
                    </span>
                  )}
                </div>
              ))}
              {card.events.length > 3 && (
                <div
                  onClick={() => toggleEventsExpanded(card.id)}
                  style={{
                    fontSize: "11px",
                    color: "#0066cc",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {expandedEvents[card.id]
                    ? "Show less"
                    : `+${card.events.length - 3} more events`}
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Feedback from Likes */}
        {card.userFeedback && card.userFeedback.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ fontSize: "12px" }}>User Feedback:</strong>
            <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {card.userFeedback.map((feedback, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: "12px",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                  }}
                >
                  <div style={{ marginBottom: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                      style={{
                        color: feedback.value === 1 ? "#4caf50" : "#f44336",
                        fontWeight: "600",
                      }}
                    >
                      {feedback.value === 1 ? "👍" : "👎"}
                    </span>
                    {feedback.path && feedback.path.length > 0 && (
                      <span style={{ color: "#999", fontSize: "11px" }}>
                        [{feedback.path.join(", ")}]
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#333", fontStyle: "italic" }}>
                    "{feedback.feedback}"
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: "8px",
            padding: "10px",
            backgroundColor: "white",
            borderRadius: "4px",
          }}
        >
          <strong style={{ fontSize: "12px" }}>Content:</strong>
          <div style={{ marginTop: "6px" }}>{renderCardContent(card)}</div>
        </div>
      </div>
    );
  };

  const renderCardContent = (card) => {
    const isExpanded = expandedCards[card.id];
    const content = card.content;

    if (!content) return <em>No content</em>;

    let displayContent;
    if (typeof content === "string") {
      displayContent = content;
    } else if (Array.isArray(content)) {
      displayContent = content.join("\n\n");
    } else if (typeof content === "object") {
      displayContent = JSON.stringify(content, null, 2);
    } else {
      displayContent = String(content);
    }

    const isLong = displayContent.length > 2000;
    const preview = isLong && !isExpanded ? displayContent.slice(0, 2000) + "..." : displayContent;

    return (
      <div>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            margin: 0,
            fontFamily: "inherit",
            fontSize: "14px",
          }}
        >
          {preview}
        </pre>
        {isLong && (
          <button
            onClick={() => toggleCardExpanded(card.id)}
            style={{
              marginTop: "8px",
              padding: "4px 8px",
              fontSize: "12px",
              background: "#f0f0f0",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {isExpanded ? "Show Less" : "Show More"}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <Link to="/admin/experiments" style={{ color: "#0066cc", textDecoration: "none" }}>
          ← Back to Card Experiments
        </Link>
        <h1>Loading...</h1>
      </div>
    );
  }

  if (error || !experimentData) {
    return (
      <div style={{ padding: "20px" }}>
        <Link to="/admin/experiments" style={{ color: "#0066cc", textDecoration: "none" }}>
          ← Back to Card Experiments
        </Link>
        <h1>Error</h1>
        <div style={{ color: "red", marginTop: "10px" }}>
          {error || "Experiment not found"}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <Link to="/admin/experiments" style={{ color: "#0066cc", textDecoration: "none" }}>
        ← Back to Experiments
      </Link>

      <h1 style={{ marginTop: "20px" }}>{experimentId}</h1>

      {/* Experiment Overview */}
      <div
        style={{
          backgroundColor: "#f9f9f9",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "30px",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Variants</h2>
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
          {experimentData.variants.map((variant) => (
            <div
              key={variant.variationId}
              style={{
                flex: "1 1 200px",
                padding: "15px",
                backgroundColor: "white",
                border: "1px solid #ddd",
                borderRadius: "6px",
              }}
            >
              <h3 style={{ marginTop: 0, fontSize: "16px" }}>{variant.variationId}</h3>
              <div style={{ fontSize: "14px", color: "#666" }}>
                <div>Total Views: <strong>{variant.totalViews.toLocaleString()}</strong></div>
                <div>Users: <strong>{variant.userCount.toLocaleString()}</strong></div>
                <div>Anonymous: <strong>{variant.anonymousCount.toLocaleString()}</strong></div>
              </div>
            </div>
          ))}
        </div>

        {/* Word Count Statistics */}
        {experimentData.variants.some(v => v.wordCountStats) && (
          <div style={{ marginTop: "20px" }}>
            <h2
              style={{
                fontSize: "16px",
                marginBottom: "10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                userSelect: "none",
              }}
              onClick={() => setWordStatsExpanded(!wordStatsExpanded)}
            >
              <span>{wordStatsExpanded ? "▼" : "▶"}</span>
              Word Count Statistics
            </h2>
            {wordStatsExpanded && (
            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
              {(() => {
                // Determine baseline variant (control if exists, otherwise first by lexical order)
                const variantsWithStats = experimentData.variants.filter(v => v.wordCountStats);
                const baselineVariant = variantsWithStats.find(v => v.variationId.toLowerCase() === 'control') ||
                  [...variantsWithStats].sort((a, b) => a.variationId.localeCompare(b.variationId))[0];

                const calculatePercentChange = (current, baseline) => {
                  if (!baseline || baseline === 0) return null;
                  return Math.round(((current - baseline) / baseline) * 100);
                };

                const formatPercentChange = (pct) => {
                  if (pct === null || pct === 0) return null;
                  const sign = pct > 0 ? '+' : '';
                  const color = pct > 0 ? '#2e7d32' : '#c62828';
                  return <span style={{ color, fontSize: '11px', marginLeft: '4px' }}>({sign}{pct}%)</span>;
                };

                return variantsWithStats.map((variant) => {
                  const { type, stats } = variant.wordCountStats;
                  const isBaseline = variant.variationId === baselineVariant.variationId;

                  return (
                    <div
                      key={variant.variationId}
                      style={{
                        flex: "1 1 300px",
                        padding: "15px",
                        backgroundColor: "white",
                        border: isBaseline ? "2px solid #1976d2" : "1px solid #ddd",
                        borderRadius: "6px",
                      }}
                    >
                      <h3 style={{ marginTop: 0, fontSize: "14px", marginBottom: "10px" }}>
                        {variant.variationId}
                        {isBaseline && <span style={{ color: "#1976d2", fontSize: "11px", marginLeft: "6px" }}>(baseline)</span>}
                      </h3>

                      {type === 'string' ? (
                        // Simple string content stats
                        <div style={{ fontSize: "13px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 12px" }}>
                            <span style={{ color: "#888" }}>Avg:</span>
                            <div>
                              <strong>{stats.avg} words</strong>
                              {!isBaseline && formatPercentChange(calculatePercentChange(stats.avg, baselineVariant.wordCountStats.stats.avg))}
                            </div>
                            <span style={{ color: "#888" }}>Median:</span>
                            <div>
                              <strong>{stats.median} words</strong>
                              {!isBaseline && formatPercentChange(calculatePercentChange(stats.median, baselineVariant.wordCountStats.stats.median))}
                            </div>
                            <span style={{ color: "#888" }}>P75:</span>
                            <div>
                              <strong>{stats.p75} words</strong>
                              {!isBaseline && formatPercentChange(calculatePercentChange(stats.p75, baselineVariant.wordCountStats.stats.p75))}
                            </div>
                            <span style={{ color: "#888" }}>P95:</span>
                            <div>
                              <strong>{stats.p95} words</strong>
                              {!isBaseline && formatPercentChange(calculatePercentChange(stats.p95, baselineVariant.wordCountStats.stats.p95))}
                            </div>
                            <span style={{ color: "#888" }}>Cards:</span>
                            <strong>{stats.count}</strong>
                          </div>
                        </div>
                      ) : (
                        // Object content stats (per key)
                        <div style={{ fontSize: "12px" }}>
                          {Object.entries(stats).map(([key, keyStats]) => {
                            const baselineKeyStats = baselineVariant.wordCountStats.stats[key];

                            return (
                              <div
                                key={key}
                                style={{
                                  marginBottom: "12px",
                                  paddingBottom: "8px",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <div style={{ fontWeight: "600", marginBottom: "4px", color: "#333" }}>
                                  {key}:
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 8px", paddingLeft: "8px" }}>
                                  <span style={{ color: "#888" }}>Avg:</span>
                                  <div>
                                    <span>{keyStats.avg} words</span>
                                    {!isBaseline && baselineKeyStats && formatPercentChange(calculatePercentChange(keyStats.avg, baselineKeyStats.avg))}
                                  </div>
                                  <span style={{ color: "#888" }}>Median:</span>
                                  <div>
                                    <span>{keyStats.median} words</span>
                                    {!isBaseline && baselineKeyStats && formatPercentChange(calculatePercentChange(keyStats.median, baselineKeyStats.median))}
                                  </div>
                                  <span style={{ color: "#888" }}>P75:</span>
                                  <div>
                                    <span>{keyStats.p75} words</span>
                                    {!isBaseline && baselineKeyStats && formatPercentChange(calculatePercentChange(keyStats.p75, baselineKeyStats.p75))}
                                  </div>
                                  <span style={{ color: "#888" }}>P95:</span>
                                  <div>
                                    <span>{keyStats.p95} words</span>
                                    {!isBaseline && baselineKeyStats && formatPercentChange(calculatePercentChange(keyStats.p95, baselineKeyStats.p95))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            )}
          </div>
        )}

      </div>

      {/* Comparison Mode Toggle */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={comparisonMode}
            onChange={(e) => setComparisonMode(e.target.checked)}
            style={{ marginRight: "8px" }}
          />
          <span style={{ fontSize: "16px", fontWeight: "500" }}>
            Compare Variants Side by Side
          </span>
        </label>
      </div>

      {/* Variant Selectors and Filters */}
      <div style={{ marginBottom: "20px", display: "flex", gap: "30px", flexWrap: "wrap" }}>
        {/* Column 1 Controls */}
        <div style={{ flex: comparisonMode ? "1 1 45%" : "1 1 100%", display: "flex", flexDirection: "column", gap: "10px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
          <h3 style={{ margin: 0, fontSize: "16px" }}>
            {comparisonMode ? "Variant 1" : "Variant"}
          </h3>
          <div>
            <label style={{ fontSize: "14px", fontWeight: "500", marginRight: "10px" }}>
              Variant:
            </label>
            <select
              value={selectedVariant || ""}
              onChange={(e) => setSelectedVariant(e.target.value)}
              style={{
                padding: "6px 10px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              {experimentData.variants.map((variant) => (
                <option key={variant.variationId} value={variant.variationId}>
                  {variant.variationId} ({variant.totalViews} views)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: "14px", fontWeight: "500", marginRight: "10px" }}>
              Filter:
            </label>
            <select
              value={metricEventFilter}
              onChange={(e) => setMetricEventFilter(e.target.value)}
              style={{
                padding: "6px 10px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <option value="">All Cards</option>
              <option value="no-papi">No PAPI (no like/copy/insert/favorite)</option>
              {variantMetricEvents.map((event) => (
                <option key={event.type} value={event.type}>
                  {event.type} ({event.uniqueCards} cards)
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Column 2 Controls (only in comparison mode) */}
        {comparisonMode && (
          <div style={{ flex: "1 1 45%", display: "flex", flexDirection: "column", gap: "10px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
            <h3 style={{ margin: 0, fontSize: "16px" }}>Variant 2</h3>
            <div>
              <label style={{ fontSize: "14px", fontWeight: "500", marginRight: "10px" }}>
                Variant:
              </label>
              <select
                value={selectedVariant2 || ""}
                onChange={(e) => setSelectedVariant2(e.target.value)}
                style={{
                  padding: "6px 10px",
                  fontSize: "14px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                {experimentData.variants.map((variant) => (
                  <option key={variant.variationId} value={variant.variationId}>
                    {variant.variationId} ({variant.totalViews} views)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: "14px", fontWeight: "500", marginRight: "10px" }}>
                Filter:
              </label>
              <select
                value={metricEventFilter2}
                onChange={(e) => setMetricEventFilter2(e.target.value)}
                style={{
                  padding: "6px 10px",
                  fontSize: "14px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <option value="">All Cards</option>
                <option value="no-papi">No PAPI (no like/copy/insert/favorite)</option>
                {variantMetricEvents2.map((event) => (
                  <option key={event.type} value={event.type}>
                    {event.type} ({event.uniqueCards} cards)
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Cards Section */}
      <div style={{ display: "flex", gap: "20px", flexWrap: comparisonMode ? "nowrap" : "wrap" }}>
        {renderCardColumn({
          variant: selectedVariant,
          cards,
          cardsLoading,
          pagination,
          setPagination,
          metricEventFilter,
          setMetricEventFilter,
          variantMetricEvents,
          columnTitle: comparisonMode ? selectedVariant : "Cards",
          isBaselineColumn: true,
          baselineMetricEvents: null,
          baselineTotalCards: null,
        })}

        {comparisonMode && renderCardColumn({
          variant: selectedVariant2,
          cards: cards2,
          cardsLoading: cardsLoading2,
          pagination: pagination2,
          setPagination: setPagination2,
          metricEventFilter: metricEventFilter2,
          setMetricEventFilter: setMetricEventFilter2,
          variantMetricEvents: variantMetricEvents2,
          columnTitle: selectedVariant2,
          isBaselineColumn: false,
          baselineMetricEvents: variantMetricEvents,
          baselineTotalCards: pagination.total,
        })}
      </div>

      {/* Statistics Modal */}
      {renderStatsModal()}
    </div>
  );
}
